import { lazy, Suspense } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import {
  HOME,
  ORDERS,
  PRODUCTS,
  ORDER,
  NOTIFICATIONS,
  SUPPORT,
  SETTINGS,
  SETTINGS_BUNDLES_SURFACES,
  BRANDS,
  USERS,
  NFT_COLLECTIONS,
  NFT_DROPS,
  NFT_PERKS,
  ADD_NFT_COLLECTION,
  EDIT_NFT_COLLECTION,
  EDIT_NFT_DROP,
  ADD_NFT_DROP,
  ADD_NFT_PERK,
  EDIT_NFT_PERK,
  BRAND_SETTINGS,
  BRAND_PRODUCTS,
  BRAND_USERS,
  BRAND_CUSTOMERS,
  BRAND_DISPENSARIES,
  BRAND_DISPENSARY_METRC,
  BRAND_BATCHES,
  GAMES,
  ADD_GAME,
  EDIT_GAME,
  GAMES_USERS,
  CATEGORIES,
  BRAND_CATEGORIES,
  STATES,
  ADMINS,
  BRAND_CUSTOMERS_INFO,
  BRAND_DASHBOARD,
  BRAND_ORDER,
  BRAND_ORDER_WITHOUT_BRAND_ID,
  BRAND_ORDERS_WITH_TYPE,
  BRAND_ORDERS_WITH_TYPE_WITHOUT_BRAND_ID,
  EMPLOYEES,
  ROLES,
  BRAND_PRODUCTS_LOGS,
  DRIVER_ORDERS,
  BRAND_REWARDS,
  BRAND_REWARDS_LOG,
  BRAND_REWARDS_LOG_INFO,
  BRAND_MESSAGES,
  PASSWORD_FORGET,
  PASSWORD_RESET,
  QR_SEQUENCES,
} from "./constants";
import useAuth from "./hooks/useAuth";

const Brands = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "brandsPage" */ "./pages/Brands"))
);
const NotFound = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "notFoundPage" */ "./pages/NotFound")
  )
);
const OrdersCollectibles = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "ordersCollectiblesPage" */ "./pages/OrdersCollectibles"
    )
  )
);
const Products = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "productsPage" */ "./pages/Products")
  )
);
const NftCollections = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "nftCollectionsPage" */ "./pages/Nft/Collections"
    )
  )
);
const AddEditNftCollections = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "addEditNftCollectionsPage" */ "./pages/Nft/Collections/AddEdit"
    )
  )
);
const NftDrops = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "nftDropsPage" */ "./pages/Nft/Drops")
  )
);
const AddEditNftDrops = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "addEditNftDropsPage" */ "./pages/Nft/Drops/AddEdit"
    )
  )
);
const NftPerks = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "nftPerksPage" */ "./pages/Nft/Perks")
  )
);
const AddEditNftPerk = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "addEditNftPerksPage" */ "./pages/Nft/Perks/AddEdit"
    )
  )
);
const Order = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "orderPage" */ "./pages/Order"))
);
const Notifications = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "notificationsPage" */ "./pages/Notifications")
  )
);
const Support = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "supportPage" */ "./pages/Support")
  )
);
const Config = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "configPage" */ "./pages/Config"))
);
const BundlesSurfacesPage = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "bundlesSurfacesPage" */ "./pages/Settings/Bundles/Surfaces"
    )
  )
);
const Users = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "usersPage" */ "./pages/Users"))
);
const BrandDashboard = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandDashboardPage" */ "./pages/Brand/Dashboard"
    )
  )
);
const BrandProducts = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandProductsPage" */ "./pages/Brand/Products")
  )
);
const BrandEmployees = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandEmployeesPage" */ "./pages/Brand/Employees"
    )
  )
);
const BrandCustomers = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandCustomersPage" */ "./pages/Brand/Customers"
    )
  )
);

const Admins = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "AdminsPage" */ "./pages/Admins"))
);

const BrandMetrc = lazy(() =>
  import(/* webpackChunkName: "brandMetrcPage" */ "./pages/Brand/Metrc")
);
const BrandDispensaries = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandDispensariesPage" */ "./pages/Brand/Dispensaries"
    )
  )
);
const BrandBatches = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandLoyaltyBatchesPage" */ "./pages/Brand/Batches"
    )
  )
);
const BrandRewards = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandRewardsPage" */ "./pages/Brand/Rewards")
  )
);
const BrandRewardsLog = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandRewardsLogPage" */ "./pages/Brand/RewardsLog"
    )
  )
);
const BrandRewardsLogInfo = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandRewardsLogInfoPage" */ "./pages/Brand/RewardsLogInfo"
    )
  )
);
const BrandSettings = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandSettingsPage" */ "./pages/Brand/Settings")
  )
);
const BrandOrders = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandOrdersPage" */ "./pages/Brand/Orders")
  )
);
const BrandOrder = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandOrderPage" */ "./pages/Brand/Order")
  )
);
const Games = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "gamesPage" */ "./pages/Games"))
);
const AddEditGame = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "addEditGamePage" */ "./pages/Games/AddEdit")
  )
);
const GamesUsers = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "gamesUsersPage" */ "./pages/Games/Users")
  )
);
const Categories = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "categoriesPage" */ "./pages/Categories")
  )
);
const BrandCategories = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandCategoriesPage" */ "./pages/Brand/Categories"
    )
  )
);
const States = lazy(() =>
  lazyRetry(() => import(/* webpackChunkName: "statesPage" */ "./pages/States"))
);
const Employees = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "employeesPage" */ "./pages/Employees")
  )
);
const DriverOrders = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "driverOrdersPage" */ "./pages/Driver/Orders")
  )
);
const DriverOrder = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "driverOrdersPage" */ "./pages/Driver/Order")
  )
);
const BrandProductsLogs = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "brandProductsLogsPage" */ "./pages/Brand/ProductsLogs"
    )
  )
);
const Messages = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "brandMessagesPage" */ "./pages/Brand/Messages")
  )
);
const PasswordForgot = lazy(() =>
  lazyRetry(() =>
    import(
      /* webpackChunkName: "passwordForgotPage" */ "./pages/PasswordForgot"
    )
  )
);
const PasswordReset = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "passwordResetPage" */ "./pages/PasswordReset")
  )
);
const LoginNew = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "loginNewPage" */ "./pages/LoginNew")
  )
);
const QrSequences = lazy(() =>
  lazyRetry(() =>
    import(/* webpackChunkName: "qrSequencesPage" */ "./pages/QrSequences")
  )
);

const lazyRetry = function (componentImport) {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem("retry-lazy-refreshed") || "false"
    );
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem("retry-lazy-refreshed", "false");
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          window.sessionStorage.setItem("retry-lazy-refreshed", "true");
          return window.location.reload();
        }
        reject(error);
      });
  });
};

export const AppRouter = ({ user }) => {
  const { role } = useAuth();

  const routes = [
    {
      path: HOME,
      component: BrandDashboard,
      access: [ROLES.brandManager],
      exact: true,
    },
    {
      path: HOME,
      component: BrandOrders,
      access: [ROLES.brandBudtender],
      exact: true,
    },
    {
      path: HOME,
      component: DriverOrders,
      access: [ROLES.brandDriver],
      exact: true,
    },
    {
      path: PRODUCTS,
      component: Products,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRANDS,
      component: Brands,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: NFT_COLLECTIONS,
      component: NftCollections,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: [ADD_NFT_COLLECTION, EDIT_NFT_COLLECTION],
      component: AddEditNftCollections,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: NFT_DROPS,
      component: NftDrops,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: [ADD_NFT_DROP, EDIT_NFT_DROP],
      component: AddEditNftDrops,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: NFT_PERKS,
      component: NftPerks,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: [ADD_NFT_PERK, EDIT_NFT_PERK],
      component: AddEditNftPerk,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: SETTINGS,
      component: Config,
      access: [ROLES.superAdmin, ROLES.admin],
      exact: true,
    },
    {
      path: SETTINGS_BUNDLES_SURFACES,
      component: BundlesSurfacesPage,
      access: [ROLES.superAdmin],
    },
    {
      path: ORDERS,
      component: OrdersCollectibles,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: ORDER,
      component: Order,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRAND_CUSTOMERS_INFO,
      component: BrandCustomers,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRANDS,
      component: Brands,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: USERS,
      component: Users,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: NOTIFICATIONS,
      component: Notifications,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: SUPPORT,
      component: Support,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: ADMINS,
      component: Admins,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: STATES,
      component: States,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRAND_DASHBOARD,
      component: BrandDashboard,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_PRODUCTS,
      component: BrandProducts,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
      exact: true,
    },
    {
      path: BRAND_PRODUCTS_LOGS,
      component: BrandProductsLogs,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRAND_ORDERS_WITH_TYPE,
      component: BrandOrders,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_ORDER,
      component: BrandOrder,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_USERS,
      component: BrandEmployees,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_USERS,
      component: BrandCustomers,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_CUSTOMERS,
      component: BrandCustomers,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_DISPENSARY_METRC,
      component: BrandMetrc,
      access: [ROLES.superAdmin],
    },
    {
      path: BRAND_DISPENSARIES,
      component: BrandDispensaries,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_BATCHES,
      component: BrandBatches,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_REWARDS,
      component: BrandRewards,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
      exact: true,
    },
    {
      path: BRAND_REWARDS_LOG,
      component: BrandRewardsLog,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
      exact: true,
    },
    {
      path: BRAND_REWARDS_LOG_INFO,
      component: BrandRewardsLogInfo,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_SETTINGS,
      component: BrandSettings,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: BRAND_ORDERS_WITH_TYPE_WITHOUT_BRAND_ID,
      component: BrandOrders,
      access: [ROLES.brandBudtender],
    },
    {
      path: GAMES,
      component: Games,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: [ADD_GAME, EDIT_GAME],
      component: AddEditGame,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: GAMES_USERS,
      component: GamesUsers,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: CATEGORIES,
      component: Categories,
      access: [ROLES.superAdmin, ROLES.admin],
    },
    {
      path: BRAND_CATEGORIES,
      component: BrandCategories,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: EMPLOYEES,
      component: Employees,
      access: [ROLES.superAdmin],
    },
    {
      path: BRAND_ORDER_WITHOUT_BRAND_ID,
      component: BrandOrder,
      access: [ROLES.brandBudtender],
    },
    {
      path: BRAND_MESSAGES,
      component: Messages,
      access: [ROLES.superAdmin, ROLES.admin, ROLES.brandManager],
    },
    {
      path: DRIVER_ORDERS,
      component: DriverOrders,
      access: [ROLES.brandDriver],
    },
    {
      path: BRAND_ORDER_WITHOUT_BRAND_ID,
      component: DriverOrder,
      access: [ROLES.brandDriver],
    },
    {
      path: QR_SEQUENCES,
      component: QrSequences,
      access: [ROLES.superAdmin],
      exact: true,
    },
    {
      path: "*",
      component: NotFound,
      access: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.brandManager,
        ROLES.brandBudtender,
        ROLES.brandDriver,
      ],
    },
  ];

  return (
    <Suspense fallback={null}>
      {user ? (
        <Switch>
          <Redirect from={HOME} to={BRANDS} exact />
          {routes.map(
            (route) =>
              route.access.includes(role) && (
                <Route
                  key={Array.isArray(route.path) ? route.path[0] : route.path}
                  path={route.path}
                  component={route.component}
                  exact={route.exact}
                />
              )
          )}
        </Switch>
      ) : (
        <Switch>
          <Route path={PASSWORD_FORGET} component={PasswordForgot} />
          <Route path={PASSWORD_RESET} component={PasswordReset} />
          <Route path={`${ORDER}/:token`} component={Order} />
          <Route exact path="*" component={LoginNew} />
        </Switch>
      )}
    </Suspense>
  );
};
